import { IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { excludeValues, formatDate } from '@/components/DataGrid/utils';
import {
  IOffenseOption,
  IOffenseOptionItem,
  PropertyStatusList,
} from '@/models';
import {
  getOffenseOptionItems,
  returnCleanValue,
} from '@/pages/Kases/KaseCrimes/utilities';

const number = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const name = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const typeTag = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const itemNumber = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const type = (filteredOptions?: IOffenseOption[]): Partial<GridColDef> => {
  const PropertyTypeList = getOffenseOptionItems(
    'propertyType',
    filteredOptions || [],
  ) as Map<string, IOffenseOptionItem>;

  return {
    renderCell: (params) =>
      [...PropertyTypeList.values()].find((val) => val.key === params.row.type)
        ?.label,
    filterable: false,
    valueGetter: (_, row) =>
      [...PropertyTypeList.values()].find((val) => val.key === row.type)?.label,
    sortable: false,
  };
};

const involvement = (
  filteredOptions?: IOffenseOption[],
): Partial<GridColDef> => ({
  renderCell: (params) => {
    if (!filteredOptions || !filteredOptions.length) {
      return '';
    }

    const involvementOptions = getOffenseOptionItems(
      'involvement',
      filteredOptions,
    ) as Map<string, IOffenseOptionItem>;

    return returnCleanValue(
      involvementOptions?.get(params.row.involvement)?.label,
    );
  },
  filterable: false,
  sortable: false,
  valueGetter: (_, row) => {
    if (!filteredOptions || !filteredOptions.length) {
      return '';
    }

    const involvementOptions = getOffenseOptionItems(
      'involvement',
      filteredOptions,
    ) as Map<string, IOffenseOptionItem>;

    return returnCleanValue(involvementOptions?.get(row.involvement)?.label);
  },
});

const dateLogged = (): Partial<GridColDef> => ({
  filterOperators: getGridDateOperators(true),
  renderCell: (params) => formatDate(params.row.loggedInfo?.loggedDate),
  valueGetter: (_, row) => row.loggedInfo?.loggedDate,
});

const loggedBy = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const tempLoggedBy = params.row.loggedInfo?.loggedBy;
    return tempLoggedBy ? tempLoggedBy.fullName : '';
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['equals', 'doesNotEqual', 'isAnyOf']),
  ),
  valueGetter: (_, row) => row.loggedInfo?.loggedBy?.fullName,
});

const status = (): Partial<GridColDef> => ({
  renderCell: (params) => PropertyStatusList.get(params.row.status),
  filterable: false,
});

const agency = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  filterable: false,
  renderCell: (params) => params.row.kase.agency.name,
  valueGetter: (_, row) => row.kase.agency.name,
});

const loggedAddress = (): Partial<GridColDef> => ({
  headerName: 'Inventory Location',
  filterOperators: getGridStringOperators().filter(excludeValues(['isAnyOf'])),
  renderCell: (params) =>
    params.row.custodyActionResultInfo?.inventoryLocation?.name || '',
});

const kase = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  renderCell: (params) => params.row.kase.number,
  valueGetter: (_, row) => row.kase.number,
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => (
    <div className="w-full">
      <IconButton
        aria-label="actions"
        onClick={(e) => {
          e.stopPropagation();
          openMenu?.(e, params.row);
        }}
      >
        <IcDotSpread />
      </IconButton>
    </div>
  ),
});

export const propertyEvidenceCols = (
  filteredOptions?: IOffenseOption[],
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
) => ({
  number,
  name,
  typeTag,
  itemNumber,
  type: () => type(filteredOptions),
  involvement: () => involvement(filteredOptions),
  dateLogged,
  loggedBy,
  status,
  loggedAddress,
  kase,
  agency,
  actions: () => actions(openMenu),
});
